/**
 * Gift Box Builder
 * ================
 * Inline image-tile selection for character + card, with message field
 * that slides in once a card is chosen.
 *
 * Cart line item properties (visible in admin order view):
 *   "Character"      → selected character product title
 *   "Card"           → selected card product title
 *   "Message"        → custom message (omitted if blank)
 *   "Gift Box Includes" → comma-separated included product titles (omitted if none)
 *   "_character_id"  → character product ID (hidden from storefront)
 *   "_card_id"       → card product ID (hidden from storefront)
 *
 * Cart attributes (used for order confirmation email template access):
 *   "{sanitizedKey}-character" → selected character title
 *   "{sanitizedKey}-card"      → selected card title
 *   "{sanitizedKey}-message"   → custom message (omitted if blank)
 *
 *   {sanitizedKey} is the bundle's cart line item key with ':' replaced by '-',
 *   e.g. "40934656008258-52c18f04". Prefixing by key allows multiple gift boxes
 *   per cart and clean removal when the item is deleted.
 */

(function () {
  'use strict';

  function init() {
    document.querySelectorAll('.gift-box-builder').forEach(initBuilder);
  }

  function initBuilder(builderEl) {
    const variantId = builderEl.dataset.variantId;
    if (!variantId) {
      console.warn('[GiftBox] No variant ID on .gift-box-builder — check your template.');
      return;
    }

    const state = {
      character: null,  // { id, title, variantId }
      card: null,       // { id, title, variantId }
      message: '',
    };

    // ── DOM refs ──────────────────────────────────────────────────────────
    const messageStep     = builderEl.querySelector('[data-step="message"]');
    const messageTextarea = builderEl.querySelector('.gift-box-builder__message-textarea');
    const charCountEl     = builderEl.querySelector('[data-char-count]');
    const atcButton       = builderEl.querySelector('[data-atc-button]');
    const atcLabel        = builderEl.querySelector('[data-atc-label]');
    const validationEl    = builderEl.querySelector('[data-validation-errors]');

    // ── Radio tile change handlers ────────────────────────────────────────

    builderEl.querySelectorAll('input[type="radio"][data-option-type]').forEach(radio => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        const type = radio.dataset.optionType;

        const selection = {
          id: radio.dataset.productId,
          title: radio.dataset.productTitle,
          variantId: radio.dataset.variantId,
        };

        state[type] = selection;

        // Update the "Character: X" / "Card: X" label
        const labelEl = builderEl.querySelector(`[data-selection-label="${type}"]`);
        if (labelEl) labelEl.textContent = selection.title;

        // Sync .is-selected class on the parent <label> for browsers
        // that don't support :has() (older Safari, Firefox < 121)
        const group = builderEl.querySelector(`[data-options="${type}"]`);
        if (group) {
          group.querySelectorAll('.gift-box-tile').forEach(tile => {
            tile.classList.toggle('is-selected', tile.contains(radio));
          });
        }

        // Reveal message field when a card is chosen
        if (type === 'card') {
          revealMessageStep();
        }

        clearErrors();
      });
    });

    // ── Message step reveal ───────────────────────────────────────────────

    function revealMessageStep() {
      if (!messageStep || messageStep.classList.contains('is-visible')) return;
      // Remove hidden attr first so the element is in the layout, then
      // add is-visible on next tick so the CSS transition fires
      messageStep.removeAttribute('hidden');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          messageStep.classList.add('is-visible');
        });
      });
    }

    // ── Message counter ───────────────────────────────────────────────────

    if (messageTextarea && charCountEl) {
      messageTextarea.addEventListener('input', () => {
        state.message = messageTextarea.value;
        charCountEl.textContent = messageTextarea.value.length;
      });
    }

    // ── Validation ────────────────────────────────────────────────────────

    function validate() {
      const errors = [];
      if (!state.character) errors.push('Please choose a character.');
      if (!state.card)      errors.push('Please choose a greeting card.');
      return errors;
    }

    function showErrors(errors) {
      if (!validationEl) return;
      validationEl.innerHTML = errors.map(e => `<p style="margin:0 0 0.2rem">${e}</p>`).join('');
    }

    function clearErrors() {
      if (validationEl) validationEl.innerHTML = '';
    }

    // ── Add to cart ───────────────────────────────────────────────────────

    async function addToCart() {
      const errors = validate();
      if (errors.length) {
        showErrors(errors);
        validationEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      clearErrors();
      setLoading(true);

      const msg = state.message ? state.message.trim() : '';

      const properties = {
        'Character':     `${state.character.title} Plush & Pin`,
        'Card':          state.card.title,
        '_character_id': state.character.id,
        '_card_id':      state.card.id,
      };
      if (msg.length > 0) properties['Message'] = msg;

      const includedItemsJson = builderEl.dataset.includedItems;
      if (includedItemsJson) {
        properties['Gift Box Includes'] = JSON.parse(includedItemsJson).join(', ');
      }

      try {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1, properties }]
          }),
        });

        const addData = await addResponse.json();

        if (!addResponse.ok) {
          showErrors([addData.description || addData.message || 'Something went wrong. Please try again.']);
          setLoading(false);
          return;
        }

        // Find the bundle item in the response and set cart attributes so the
        // selections are accessible in the order confirmation email template.
        // Shopify Bundles may return multiple items; match by variant ID.
        const responseItems = addData.items || [addData];
        const bundleItem = responseItems.find(
          item => Number(item.variant_id || item.id) === Number(variantId)
        ) || responseItems[0];

        const attrPrefix = (bundleItem.key || String(bundleItem.variant_id || bundleItem.id)).replace(':', '-');
        const cartAttributes = {
          [`${attrPrefix}-character`]: `${state.character.title} Plush & Pin`,
          [`${attrPrefix}-card`]:      state.card.title,
        };
        if (msg.length > 0) cartAttributes[`${attrPrefix}-message`] = msg;

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ attributes: cartAttributes }),
        });

        onAddedToCart();

      } catch (err) {
        console.error('[GiftBox] Cart error:', err);
        showErrors(['Something went wrong. Please try again.']);
        setLoading(false);
      }
    }

    function setLoading(isLoading) {
      if (!atcButton || !atcLabel) return;
      atcButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      atcLabel.textContent = isLoading
        ? 'Adding…'
        : (atcButton.dataset.originalLabel || 'Add Gift Box to Cart');
    }

    function onAddedToCart() {
      setLoading(false);
      // Prestige cart drawer events
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { openDrawer: true } })
      );
      document.body.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    }

    // ── Attach ATC ────────────────────────────────────────────────────────

    if (atcButton) {
      if (atcLabel) atcButton.dataset.originalLabel = atcLabel.textContent;
      atcButton.addEventListener('click', addToCart);
    }
  }

  // ── Cart attribute cleanup ────────────────────────────────────────────────
  // When a gift box is removed from the cart, clear its prefixed attributes so
  // they don't bleed into subsequent orders placed from the same browser session.
  document.documentElement.addEventListener('cart:change', async (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || !cart.attributes) return;

    // Build a set of sanitized keys for every item currently in the cart
    const liveKeys = new Set(
      (cart.items || []).map(item =>
        (item.key || String(item.variant_id || item.id)).replace(':', '-')
      )
    );

    const toClear = {};
    let dirty = false;

    for (const [name, value] of Object.entries(cart.attributes)) {
      if (!value) continue;
      if (!name.endsWith('-character') && !name.endsWith('-card') && !name.endsWith('-message')) continue;
      // Strip the field suffix to recover the sanitized cart item key
      const prefix = name.slice(0, name.lastIndexOf('-'));
      if (!liveKeys.has(prefix)) {
        toClear[name] = '';
        dirty = true;
      }
    }

    if (!dirty) return;

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ attributes: toClear }),
      });
    } catch (err) {
      console.error('[GiftBox] Failed to clear stale cart attributes:', err);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

// ── Info Modal ────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  function initModal() {
    const modal = document.getElementById('gift-box-info-modal');
    if (!modal) return;

    const overlay  = modal.querySelector('.gift-box-modal__overlay');
    const closeBtn = modal.querySelector('.gift-box-modal__close');
    const titleEl  = modal.querySelector('.gift-box-modal__title');
    const descEl   = modal.querySelector('.gift-box-modal__description');
    const track    = modal.querySelector('.gift-box-modal__track');
    const prevBtn  = modal.querySelector('.gift-box-modal__prev');
    const nextBtn  = modal.querySelector('.gift-box-modal__next');
    const dotsEl   = modal.querySelector('.gift-box-modal__dots');

    let images = [];
    let currentIndex = 0;

    function open(btn) {
      images = JSON.parse(btn.dataset.images || '[]');
      titleEl.textContent = btn.dataset.title || '';
      descEl.innerHTML = btn.dataset.description || '';
      buildCarousel();
      goTo(0);
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }

    function buildCarousel() {
      track.innerHTML = '';
      dotsEl.innerHTML = '';
      const showControls = images.length > 1;

      images.forEach(function (src, i) {
        var slide = document.createElement('div');
        slide.className = 'gift-box-modal__slide';
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        slide.appendChild(img);
        track.appendChild(slide);

        if (showControls) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gift-box-modal__dot';
          dot.setAttribute('aria-label', 'Image ' + (i + 1));
          dot.addEventListener('click', function () { goTo(i); });
          dotsEl.appendChild(dot);
        }
      });

      prevBtn.style.display = showControls ? '' : 'none';
      nextBtn.style.display = showControls ? '' : 'none';
    }

    function goTo(index) {
      currentIndex = Math.max(0, Math.min(index, images.length - 1));
      track.style.transform = 'translateX(-' + (currentIndex * 100) + '%)';
      dotsEl.querySelectorAll('.gift-box-modal__dot').forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === currentIndex);
      });
      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex === images.length - 1;
    }

    document.addEventListener('click', function (e) {
      var tile = e.target.closest('.gift-box-tile');
      if (!tile) return;

      var infoBtn = tile.querySelector('.gift-box-tile__info-btn');
      if (!infoBtn) return;

      // Included-product tiles have no selection state — any click opens the modal
      if (tile.classList.contains('gift-box-tile--static')) {
        e.preventDefault();
        open(infoBtn);
        return;
      }

      // The <label> fires a synthetic click on the radio after activating it —
      // ignore it, otherwise the modal opens on every first tap too
      if (e.target.matches('input[type="radio"]')) return;

      var radio = tile.querySelector('input[type="radio"]');

      if (radio && radio.checked) {
        // Already selected: open modal regardless of where on the tile was clicked
        e.preventDefault();
        open(infoBtn);
      } else if (e.target.closest('.gift-box-tile__info-btn') && radio) {
        // Icon clicked on unselected tile: browsers don't reliably trigger label
        // activation for button clicks, so select the tile manually
        e.preventDefault();
        radio.click();
      }
    });

    overlay.addEventListener('click', close);
    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { goTo(currentIndex - 1); });
    nextBtn.addEventListener('click', function () { goTo(currentIndex + 1); });

    document.addEventListener('keydown', function (e) {
      if (modal.hasAttribute('hidden')) return;
      if (e.key === 'Escape')     close();
      if (e.key === 'ArrowLeft')  goTo(currentIndex - 1);
      if (e.key === 'ArrowRight') goTo(currentIndex + 1);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModal);
  } else {
    initModal();
  }
})();
